/**
 * A1 addressing and named-object reference parsing.
 *
 * Kept as a leaf module — types only, no host or tool imports — so worksheet
 * discovery, the Chart Creator template probe, and their tests all resolve a
 * reference the same way. Two components that disagree about which cell
 * `='My Sheet'!$B$2` means would send writes to different places.
 */

import type { WorksheetMetadata } from "./excelService";

/** Zero-based column number to its column name: 0 -> `A`, 26 -> `AA`. */
export function columnName(column: number): string {
  let value = column + 1;
  let result = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }
  return result;
}

/** Zero-based column number for a column name, the inverse of `columnName`. */
export function columnIndex(name: string): number {
  let index = 0;
  for (const character of name.toUpperCase()) {
    index = index * 26 + (character.charCodeAt(0) - 64);
  }
  return index - 1;
}

/** A1 address for a zero-based row/column pair. */
export function cellAddress(row: number, column: number): string {
  return `${columnName(column)}${row + 1}`;
}

/** Unwrap a plain object, rejecting arrays and primitives. */
export function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * First non-empty string among `names`, matched case-insensitively. Host
 * payloads are not consistent about casing between intents, so a lookup that
 * only tried the exact key would miss fields that are plainly there.
 */
export function pickString(
  record: Record<string, unknown>,
  names: readonly string[],
): string | undefined {
  for (const name of names) {
    const value = record[name];
    if (typeof value === "string" && value.trim()) return value.trim();
    const variant = Object.keys(record).find(
      (key) => key.toLowerCase() === name.toLowerCase(),
    );
    const variantValue = variant ? record[variant] : undefined;
    if (typeof variantValue === "string" && variantValue.trim()) {
      return variantValue.trim();
    }
  }
  return undefined;
}

/** Excel quotes sheet names that contain spaces, doubling any apostrophe. */
export function stripSheetQuotes(value: string): string {
  return value.replace(/^'/, "").replace(/'$/, "").replace(/''/g, "'");
}

const SINGLE_CELL_ADDRESS_RE = /^\$?[A-Z]{1,3}\$?\d+$/i;

/** `$b$2` -> `B2`; anything that is not a single cell -> `undefined`. */
export function normalizeCellAddress(value: string): string | undefined {
  const trimmed = value.trim();
  if (!SINGLE_CELL_ADDRESS_RE.test(trimmed)) return undefined;
  return trimmed.replace(/\$/g, "").toUpperCase();
}

/**
 * Zero-based row/column of a single-cell address, the inverse of
 * `cellAddress`. Callers that need to index into a read block go through this
 * rather than splitting A1 themselves.
 */
export function parseCellCoordinates(
  address: string,
): { row: number; column: number } | undefined {
  const normalized = normalizeCellAddress(address);
  if (!normalized) return undefined;
  let split = 0;
  while (split < normalized.length && normalized[split] > "9") split += 1;
  return {
    row: Number(normalized.slice(split)) - 1,
    column: columnIndex(normalized.slice(0, split)),
  };
}

/**
 * Maps a named object's `scope` field to a worksheet name.
 *
 * `GetWorkbookNamedObjects` returns sheet-scoped names as `refersTo: "=A1"`
 * with the owning sheet carried separately in `scope` (a worksheet index;
 * `-1` means workbook scope). Without this index every sheet-scoped name is
 * unresolvable, which is why discovery used to come back empty.
 */
export interface NamedObjectScopeIndex {
  byIndex: Map<number, string>;
  byName: Map<string, string>;
}

export function buildNamedObjectScopeIndex(
  sheets: readonly WorksheetMetadata[],
): NamedObjectScopeIndex {
  const byIndex = new Map<number, string>();
  const byName = new Map<string, string>();
  for (const sheet of sheets) {
    byName.set(sheet.name.toLowerCase(), sheet.name);
    if (typeof sheet.index === "number" && !byIndex.has(sheet.index)) {
      byIndex.set(sheet.index, sheet.name);
    }
  }
  // Hosts disagree on whether `index` is 0- or 1-based, so register both
  // readings of tab order as fallbacks — but only after every host-provided
  // index is in place, or one sheet's fallback would shadow another sheet's
  // real index.
  sheets.forEach((sheet, position) => {
    if (!byIndex.has(position)) byIndex.set(position, sheet.name);
    if (!byIndex.has(position + 1)) byIndex.set(position + 1, sheet.name);
  });
  return { byIndex, byName };
}

function canonicalSheetName(
  sheetName: string,
  scopeIndex?: NamedObjectScopeIndex,
): string {
  return scopeIndex?.byName.get(sheetName.toLowerCase()) ?? sheetName;
}

function sheetNameForScope(
  scope: unknown,
  scopeIndex?: NamedObjectScopeIndex,
): string | undefined {
  if (!scopeIndex) return undefined;
  if (typeof scope === "string" && scope.trim()) {
    const named = scopeIndex.byName.get(scope.trim().toLowerCase());
    if (named) return named;
    const parsed = Number(scope);
    if (Number.isInteger(parsed)) return scopeIndex.byIndex.get(parsed);
    return undefined;
  }
  if (typeof scope !== "number" || !Number.isInteger(scope)) return undefined;
  // -1 is workbook scope. A workbook-scoped name whose reference carries no
  // sheet qualifier cannot be placed, and guessing a sheet would risk writing
  // to the wrong one.
  if (scope < 0) return undefined;
  return scopeIndex.byIndex.get(scope);
}

/**
 * Resolve a host-reported reference to one worksheet cell, or `undefined`
 * when it is anything else — a multi-cell range, a formula, a constant. The
 * caller is deciding where to write, so "not certain" has to be expressible.
 */
export function parseSingleCellReference(
  raw: unknown,
  context: { scope?: unknown; scopeIndex?: NamedObjectScopeIndex } = {},
): { sheetName: string; address: string } | undefined {
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    const record = raw as Record<string, unknown>;
    const worksheetRecord =
      asRecord(record.worksheet) ?? asRecord(record.sheet);
    const sheetName = pickString(record, [
      "worksheetName",
      "sheetName",
      "worksheet",
      "sheet",
    ]) ?? (worksheetRecord
      ? pickString(worksheetRecord, ["name", "worksheetName", "sheetName"])
      : undefined);
    const rawAddress = pickString(record, ["address", "rangeAddress", "cell"]);
    const address = rawAddress ? normalizeCellAddress(rawAddress) : undefined;
    if (sheetName && address) {
      return {
        sheetName: canonicalSheetName(sheetName, context.scopeIndex),
        address,
      };
    }
    return undefined;
  }
  if (typeof raw !== "string") return undefined;
  const cleaned = raw.trim().replace(/^=/, "");
  if (!cleaned) return undefined;

  const bang = cleaned.lastIndexOf("!");
  if (bang < 0) {
    // Unqualified reference (`=A1` / `=$A$1`): the owning sheet lives in the
    // named object's `scope`. Anything that is not a bare single-cell address
    // — `=OFFSET(...)`, a multi-cell range, a constant — is not an input cell.
    const address = normalizeCellAddress(cleaned);
    if (!address) return undefined;
    const sheetName = sheetNameForScope(context.scope, context.scopeIndex);
    return sheetName ? { sheetName, address } : undefined;
  }

  const qualifier = stripSheetQuotes(cleaned.slice(0, bang)).replace(
    /^\[[^\]]+\]/,
    "",
  );
  const address = normalizeCellAddress(cleaned.slice(bang + 1));
  if (!qualifier || !address) return undefined;
  return {
    sheetName: canonicalSheetName(qualifier, context.scopeIndex),
    address,
  };
}
